/**
 * 宠物页开场：先展示石头图，点击后播放视频，播完回调 onDone。
 * 兼容：部分机型禁止「带声自动播放」，先静音 play() 再尝试恢复声音；失败时跳过或重试。
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import stoneImg from 'asset/video/stone.png';
import firsthouziVideoUrl from 'asset/video/firsthouzi.mp4';
import './PetStoneIntro.scss';

const PetStoneIntro = ({ onDone }) => {
  const [phase, setPhase] = useState('stone');
  const [videoError, setVideoError] = useState(false);
  const videoRef = useRef(null);

  const handleStoneClick = useCallback(() => {
    setPhase('video');
    setVideoError(false);
  }, []);

  const handleVideoEnded = useCallback(() => {
    onDone?.();
  }, [onDone]);

  // 挂载后显式 play()，兼容：先静音播再尝试 unmute
  useEffect(() => {
    if (phase !== 'video') return;
    const el = videoRef.current;
    if (!el) return;

    const onEnd = () => handleVideoEnded();
    const onError = () => setVideoError(true);

    el.addEventListener('ended', onEnd);
    el.addEventListener('error', onError);

    const playWithFallback = () => {
      const p = el.play();
      if (p && typeof p.then === 'function') {
        p.then(() => {
          // 部分机型允许在 playing 后 unmute
          try {
            el.muted = false;
          } catch (_) {}
        }).catch(() => {
          el.muted = true;
          el.play().catch(() => setVideoError(true));
        });
      }
    };

    if (el.readyState >= 2) {
      playWithFallback();
    } else {
      el.addEventListener('loadeddata', playWithFallback, { once: true });
      const t = setTimeout(playWithFallback, 500);
      return () => {
        clearTimeout(t);
        el.removeEventListener('ended', onEnd);
        el.removeEventListener('error', onError);
        el.removeEventListener('loadeddata', playWithFallback);
      };
    }

    return () => {
      el.removeEventListener('ended', onEnd);
      el.removeEventListener('error', onError);
    };
  }, [phase, handleVideoEnded]);

  if (phase === 'stone') {
    return (
      <div className="pet-stone-intro pet-stone-intro--overlay">
        <div className="pet-stone-intro__mask" />
        <div className="pet-stone-intro__content" onClick={handleStoneClick} role="button" tabIndex={0} aria-label="点击解锁悟空">
          <img src={stoneImg} alt="" className="pet-stone-intro__img pet-stone-intro__img--breath" draggable={false} />
          <button type="button" className="pet-stone-intro__btn" onClick={(e) => { e.stopPropagation(); handleStoneClick(); }}>
            点击石头，解锁悟空
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pet-stone-intro pet-stone-intro--video">
      <video
        ref={videoRef}
        className="pet-stone-intro__video"
        src={firsthouziVideoUrl}
        playsInline
        muted
        preload="auto"
        disablePictureInPicture
        disableRemotePlayback
      />
      {videoError && (
        <div className="pet-stone-intro__fallback">
          <p>视频加载失败</p>
          <button type="button" className="pet-stone-intro__btn" onClick={() => { setVideoError(false); const v = videoRef.current; if (v) { v.muted = true; v.play().catch(() => setVideoError(true)); } }}>
            重试
          </button>
          <button type="button" className="pet-stone-intro__skip" onClick={onDone}>
            跳过
          </button>
        </div>
      )}
    </div>
  );
};

export default React.memo(PetStoneIntro);
